// The portal moved to seapaportal.com (Jill 2026-09-13; old address forwards
// from 2026-12-01). A browser keeps each address's work separately, so
// anything that lives only on this device at the OLD address does not follow
// the pilot across. Before the "portal has moved" screen sends anyone over,
// this file copies every unfinished slip and expense draft on the device up
// to the pilot's account, where the new address picks it up (the Trip
// Workspace and dashboard pulls in unfinishedDrafts.js).
//
// Voyage plan day notes ride along too: a note typed offline stays only on
// the device until its push runs (VoyagePlan.jsx DayNote).
//
// What it cannot carry, and the screen must say so: slips still waiting in
// the outbox (they send from here, see submitQueue.js), receipt photos still
// waiting to upload, and drafts it cannot tie to this pilot. The first two
// are counted by computeSyncSummary(), the same count Portal Support shows;
// the last is `unmatched` below.
//
// Its own inventory, not listUnfinishedDrafts(): that list hides drafts the
// pilot put away, and a draft put away offline may exist nowhere else (Codex
// on #606). Put-away drafts are carried with their stamp so they stay put
// away at the new address.

import { supabase } from "../supabaseClient";
import { readSlipAux } from "./slipDraftAux";
import { withTimeout } from "./submitQueue";
import { mergeSignaturesForward, mergeExpenseSignatureForward } from "./signatureInk";
import { readTripStatusCache, resolveTripState } from "./tripStatusCache";
import { restoreDraftVault } from "./draftVault";

export const NEW_PORTAL_URL = "https://seapaportal.com/";

// Only the production address. Preview builds and local dev never show the
// screen.
const RETIRED_HOSTS = ["seapa-dispatch-dashboard.vercel.app"];

export function onRetiredAddress() {
  try { return RETIRED_HOSTS.includes(window.location.hostname); } catch { return false; }
}

const SLIP_PREFIX = "pilotSlipDraft_";
const NOTE_RE = /^voyagePlanDayNote_(.+)_(\d{4}-\d{2}-\d{2})_(.*)$/;
const EXPENSE_PREFIX = "expenseReport_";
const ARCHIVE_KEY = "workspaceArchivedTrips"; // same maps unfinishedDrafts.js uses
const DISMISSED_EXPENSE_KEY = "dismissedExpenseDrafts";

function readJson(key) {
  try { return JSON.parse(localStorage.getItem(key)); } catch { return null; }
}

const tsOf = (v) => Number(v) || Date.parse(v) || 0;

// Put-away stamps; legacy plain-array maps carry no time, so "now".
function putAwayMap(key) {
  const raw = readJson(key);
  if (Array.isArray(raw)) return Object.fromEntries(raw.map((id) => [String(id), Date.now()]));
  return raw && typeof raw === "object" ? raw : {};
}
const stampIso = (ts) => (Number(ts) > 0 ? new Date(Number(ts)).toISOString() : null);

// Anything a pilot could have typed into an expense report. A report with
// entries but no trip, voyage, or vessel yet is still work (Codex on #606);
// only an untouched blank is skipped.
function hasExpenseWork(rep) {
  const filled = (v) => v != null && String(v).trim() !== "";
  if (filled(rep.trip_id) || filled(rep.voyage_number) || filled(rep.vessel_name)) return true;
  if (filled(rep.notes) || filled(rep.signature_base64) || filled(rep.signed_date)) return true;
  if (Array.isArray(rep.receipts) && rep.receipts.length) return true;
  const travel = ["dispatch_start", "dispatch_start_loc", "on_ship", "on_ship_loc", "off_ship", "off_ship_loc", "dispatch_end", "dispatch_end_loc"];
  if (travel.some((f) => filled(rep[f]))) return true;
  const amounts = ["air_amount", "ground_amount", "lodging_amount", "other_amount", "breakfast_amount", "lunch_amount", "dinner_amount"];
  return (rep.days || []).some((d) => d && (filled(d.date) || amounts.some((f) => filled(d[f]))));
}

// Every unfinished draft on the device, put-away ones included.
//   slips:    [{ tripId, env }]  env normalized to { timestamp, data }
//   expenses: [{ reportId, rep }]
//   notes:    [{ key, tripId, note, ts }]
//   filed:    [{ tripId, env }]  submitted slips whose full filed copy may
//             not have reached the account yet (see finalizeSlipSubmit)
function deviceInventory() {
  const slips = [];
  const filed = [];
  const expenses = [];
  const notes = [];
  const keys = [];
  try { for (let i = 0; i < localStorage.length; i++) keys.push(localStorage.key(i)); } catch { return { slips, expenses, notes, filed }; }
  const statusCache = readTripStatusCache();
  for (const key of keys) {
    if (!key || key.endsWith("_prev")) continue;
    if (key.startsWith(SLIP_PREFIX)) {
      const raw = readJson(key);
      if (!raw || typeof raw !== "object") continue;
      // A legacy draft is the bare slip with no envelope (Codex on #606): the
      // new address only reads { timestamp, data }, so wrap it.
      const env = raw.data ? raw : { timestamp: tsOf(raw.timestamp), data: raw };
      const slip = env.data;
      const tripId = String(slip.trip_id || key.slice(SLIP_PREFIX.length));
      // A submitted envelope still on the device is the filed copy whose
      // account upload failed: finalizeSlipSubmit keeps it until the account
      // confirms it, and only a sweep at THIS address retries (Codex on #606).
      if ((slip.status || "draft") === "submitted") { filed.push({ tripId, env }); continue; }
      if (resolveTripState(tripId, slip.status || "draft", statusCache).state === "filed") continue;
      if (localStorage.getItem(`pilotSlipDeleted_${tripId}`)) continue;
      slips.push({ tripId, env });
    } else if (key.startsWith(EXPENSE_PREFIX)) {
      const rep = readJson(key);
      if (!rep || typeof rep !== "object" || rep.status === "submitted") continue;
      if (!hasExpenseWork(rep)) continue;
      expenses.push({ reportId: String(rep.id || key.slice(EXPENSE_PREFIX.length)), rep });
    } else if (!key.endsWith("_ts")) {
      const m = NOTE_RE.exec(key);
      if (!m) continue;
      let note = "";
      try { note = localStorage.getItem(key) || ""; } catch { /* unreadable */ }
      let ts = 0;
      try { ts = Number(localStorage.getItem(`${key}_ts`)) || 0; } catch { /* none */ }
      // A cleared note with a time is a deletion made offline (Codex on
      // #606): it must reach the account or the old text comes back.
      if (!note.trim() && !ts) continue;
      notes.push({ key, tripId: m[1], note, ts });
    }
  }
  return { slips, expenses, notes, filed };
}

// This pilot's pilots_info id, to tie standalone expense drafts to them. A
// shared device keeps the previous pilot's work after sign-out (Codex on
// #606), so an expense draft is carried only when it is provably this
// pilot's.
async function myPilotInfoId(uid) {
  try {
    const { data, error } = await withTimeout(
      supabase.from("pilots_info").select("id").eq("auth_user_id", uid).limit(1), 8000);
    if (!error && data?.[0]?.id) return String(data[0].id);
  } catch { /* fall through to the cached roster */ }
  const cached = readJson("cached_pilots_expense");
  const me = Array.isArray(cached) ? cached.find((p) => p?.auth_user_id === uid) : null;
  return me?.id ? String(me.id) : null;
}

// A put-away made on this device holds only when nothing was edited after it
// (the app's own rule: an edit brings an entry back). Returns the stamp to
// send to the account, or null. Also used when the account already has the
// row: a draft mirrored earlier and put away offline stays put away at the
// new address (Codex on #606).
function putAwayToSend(stamp, newestEditTs, row) {
  const t = Number(stamp) || 0;
  if (!t || t < newestEditTs) return null;
  if (row?.archived_at && tsOf(row.archived_at) >= t) return null; // account already has it
  return stampIso(t);
}

async function sendPutAway(table, idCol, id, uid, iso) {
  const { error } = await withTimeout(
    supabase.from(table).update({ archived_at: iso }).eq("auth_user_id", uid).eq(idCol, id), 8000);
  if (error) throw error;
}

const ownerOf = (tripId) => { try { return localStorage.getItem("wsOwner_" + tripId); } catch { return null; } };

function clearUnsavedMarker(tripId) {
  try { localStorage.removeItem(`pilotSlipUnsaved_${tripId}`); } catch { /* ignore */ }
}

// Returns { status, total, carried, alreadySafe, failed, unmatched }
//   status: "nothing" | "done" | "partial" | "no-session" | "unreachable"
// Never deletes a draft. A cloud copy that is already as new as the device's
// is left alone unless the device holds a signature it lacks; a slip the
// account holds as filed is never touched. The stale-write triggers on
// slip_drafts and expense_drafts are the server-side backstop.
export async function carryDraftsToAccount(userId) {
  // On the fast boot path the vault restore runs in the background; a draft
  // it brings back after the scan would be missed (Codex on #606). Its reads
  // are deadlined, so this cannot hang.
  try { await restoreDraftVault(); } catch { /* best effort */ }

  const inv = deviceInventory();
  const result = { status: "nothing", total: 0, carried: 0, alreadySafe: 0, failed: 0, unmatched: 0, notesSaved: 0, signatureSaved: false, filedSaved: 0 };

  let session = null;
  try { ({ data: { session } } = await withTimeout(supabase.auth.getSession(), 3000)); } catch { /* treated as none */ }
  const uid = session?.user?.id;
  if (!uid || (userId && uid !== userId)) {
    // Signed out, or the login has not settled: never call the device clear
    // while it holds drafts nobody can save yet (Codex on #606).
    result.total = inv.slips.length + inv.expenses.length + inv.notes.length + inv.filed.length;
    return { ...result, status: result.total ? "no-session" : "nothing" };
  }

  // Only what is provably this pilot's moves. Everything else on the device
  // is counted as unmatched and named on the screen, never dropped silently:
  // a shared device keeps the previous pilot's work after sign-out.
  // Slips and notes follow the app's owner rule (an untagged trip belongs to
  // whoever is signed in).
  const mineByTrip = (tripId) => { const o = ownerOf(tripId); return !o || o === uid; };
  const slips = [];
  for (const s of inv.slips) (mineByTrip(s.tripId) ? slips.push(s) : result.unmatched++);
  const filed = [];
  for (const f of inv.filed) (mineByTrip(f.tripId) ? filed.push(f) : result.unmatched++);
  const notes = [];
  for (const n of inv.notes) (mineByTrip(n.tripId) ? notes.push(n) : result.unmatched++);

  // Expense drafts: the report's own pilot decides when it names one (the
  // editor lets any pilot be chosen, so it can disagree with the trip owner,
  // Codex on #606); otherwise the trip owner.
  const expenses = [];
  if (inv.expenses.length) {
    const myPid = await myPilotInfoId(uid);
    for (const e of inv.expenses) {
      const pid = e.rep.pilot_id ? String(e.rep.pilot_id) : null;
      const tripOwner = e.rep.trip_id ? ownerOf(String(e.rep.trip_id)) : null;
      const mine = pid ? !!myPid && pid === myPid : tripOwner === uid;
      if (mine) expenses.push(e);
      else result.unmatched++;
    }
  }

  result.total = slips.length + expenses.length + result.unmatched;

  // What the account already holds.
  let slipRows, expRows, noteRows;
  try {
    const [s, x, nt] = await Promise.all([
      slips.length || filed.length
        ? withTimeout(supabase.from("slip_drafts")
          .select("trip_id, saved_at, slip_state, archived_at, env_trip:draft->data->>trip_id")
          .in("trip_id", [...slips, ...filed].map((v) => v.tripId)), 8000)
        : { data: [] },
      expenses.length
        ? withTimeout(supabase.from("expense_drafts").select("report_id, saved_at, archived_at").in("report_id", expenses.map((v) => v.reportId)), 8000)
        : { data: [] },
      notes.length
        ? withTimeout(supabase.from("voyage_plan_day_notes").select("note_key, saved_at").in("note_key", notes.map((v) => v.key)), 8000)
        : { data: [] },
    ]);
    if (s.error || x.error || nt.error) throw s.error || x.error || nt.error;
    slipRows = new Map((s.data || []).map((r) => [String(r.trip_id), r]));
    expRows = new Map((x.data || []).map((r) => [String(r.report_id), r]));
    noteRows = new Map((nt.data || []).map((r) => [String(r.note_key), r]));
  } catch {
    return { ...result, status: "unreachable", failed: slips.length + expenses.length + notes.length + filed.length };
  }

  const archived = putAwayMap(ARCHIVE_KEY);
  const dismissed = putAwayMap(DISMISSED_EXPENSE_KEY);

  // A trip's put-away holds only if nothing on the trip, slip or linked
  // expense, on this device or in the account, was edited after it (Codex on
  // #606). Machine writes are bookkeeping, not edits (unfinishedDrafts.js).
  const tripNewest = new Map();
  const bump = (tripId, ts) => {
    if (!tripId || !ts) return;
    const k = String(tripId);
    tripNewest.set(k, Math.max(tripNewest.get(k) || 0, ts));
  };
  for (const { tripId, env } of slips) {
    if (!env.machine) bump(tripId, tsOf(env.timestamp));
    bump(tripId, tsOf(slipRows.get(tripId)?.saved_at));
  }
  for (const { reportId, rep } of expenses) {
    if (!rep.trip_id) continue;
    bump(rep.trip_id, tsOf(rep.updated_at));
    bump(rep.trip_id, tsOf(expRows.get(reportId)?.saved_at));
  }

  for (const { tripId, env } of slips) {
    const row = slipRows.get(tripId);
    if (row?.slip_state === "filed") { result.alreadySafe++; clearUnsavedMarker(tripId); continue; }
    const localTs = tsOf(env.timestamp);
    try {
      if (row && tsOf(row.saved_at) >= localTs) {
        // The account copy is as new or newer. Signatures exist nowhere but
        // the envelope, so carry any this device holds that it lacks (Codex
        // on #606); the newer copy keeps everything else and its own time.
        const { data: full, error } = await withTimeout(
          supabase.from("slip_drafts").select("draft").eq("trip_id", tripId).limit(1), 8000);
        if (error) throw error;
        const cloudEnv = full?.[0]?.draft;
        const merged = cloudEnv ? mergeSignaturesForward(env, cloudEnv) : cloudEnv;
        if (merged && merged !== cloudEnv) {
          const { error: upErr } = await withTimeout(supabase.from("slip_drafts").upsert({
            auth_user_id: uid, trip_id: tripId, draft: merged, saved_at: row.saved_at,
          }, { onConflict: "auth_user_id,trip_id" }), 10000);
          if (upErr) throw upErr;
          result.carried++;
        } else {
          result.alreadySafe++;
        }
        const away = putAwayToSend(archived[tripId], tripNewest.get(tripId) || 0, row);
        if (away) await sendPutAway("slip_drafts", "trip_id", tripId, uid, away);
        clearUnsavedMarker(tripId);
        continue;
      }
      const stamp = localTs || Date.now();
      const slip = env.data;
      const insert = {
        auth_user_id: uid,
        trip_id: tripId,
        draft: { ...env, timestamp: stamp, aux: readSlipAux(tripId) },
        saved_at: new Date(stamp).toISOString(),
        vessel_name: slip.vessel?.name || slip.vessel_name || null,
        voyage_number: slip.voyage_number || null,
      };
      // Put away on this device after its last edit: keep it put away.
      const away = putAwayToSend(archived[tripId], Math.max(stamp, tripNewest.get(tripId) || 0), row);
      if (away) insert.archived_at = away;
      const { error } = await withTimeout(
        supabase.from("slip_drafts").upsert(insert, { onConflict: "auth_user_id,trip_id" }), 10000);
      if (error) throw error;
      result.carried++;
      clearUnsavedMarker(tripId);
    } catch {
      result.failed++;
    }
  }

  for (const { reportId, rep } of expenses) {
    const row = expRows.get(reportId);
    const localTs = tsOf(rep.updated_at);
    try {
      if (row && tsOf(row.saved_at) >= localTs) {
        const { data: full, error } = await withTimeout(
          supabase.from("expense_drafts").select("draft").eq("report_id", reportId).limit(1), 8000);
        if (error) throw error;
        const cloudRep = full?.[0]?.draft;
        const merged = cloudRep ? mergeExpenseSignatureForward(rep, cloudRep) : cloudRep;
        if (merged && merged !== cloudRep) {
          const { error: upErr } = await withTimeout(supabase.from("expense_drafts").upsert({
            auth_user_id: uid, report_id: reportId, draft: merged, saved_at: row.saved_at,
          }, { onConflict: "auth_user_id,report_id" }), 10000);
          if (upErr) throw upErr;
          result.carried++;
        } else {
          result.alreadySafe++;
        }
        const putAway = rep.trip_id ? archived[String(rep.trip_id)] : dismissed[reportId];
        const newest = rep.trip_id ? tripNewest.get(String(rep.trip_id)) || 0 : Math.max(localTs, tsOf(row.saved_at));
        const away = putAwayToSend(putAway, newest, row);
        if (away) await sendPutAway("expense_drafts", "report_id", reportId, uid, away);
        continue;
      }
      const stamp = localTs || Date.now();
      const insert = {
        auth_user_id: uid,
        report_id: reportId,
        draft: { ...rep, updated_at: stamp },
        saved_at: new Date(stamp).toISOString(),
        vessel_name: rep.vessel_name || null,
        voyage_number: rep.voyage_number || null,
      };
      const putAway = rep.trip_id ? archived[String(rep.trip_id)] : dismissed[reportId];
      const away = putAwayToSend(putAway, rep.trip_id ? Math.max(stamp, tripNewest.get(String(rep.trip_id)) || 0) : stamp, row);
      if (away) insert.archived_at = away;
      const { error } = await withTimeout(
        supabase.from("expense_drafts").upsert(insert, { onConflict: "auth_user_id,report_id" }), 10000);
      if (error) throw error;
      result.carried++;
    } catch {
      result.failed++;
    }
  }

  // Filed slips whose full copy never reached the account: upload it as
  // filed, the same write finalizeSlipSubmit makes. The device copy stays.
  for (const { tripId, env } of filed) {
    const row = slipRows.get(tripId);
    const localTs = tsOf(env.timestamp) || Date.now();
    if (row?.slip_state === "filed" && row.env_trip && tsOf(row.saved_at) >= localTs) continue; // the account holds it
    const slip = env.data;
    try {
      const { error } = await withTimeout(supabase.from("slip_drafts").upsert({
        auth_user_id: uid,
        trip_id: tripId,
        draft: env,
        saved_at: new Date(localTs).toISOString(),
        vessel_name: slip.vessel?.name || slip.vessel_name || null,
        voyage_number: slip.voyage_number || null,
        slip_state: "filed",
        filed_at: new Date(localTs).toISOString(),
      }, { onConflict: "auth_user_id,trip_id" }), 10000);
      if (error) throw error;
      result.filedSaved++;
    } catch {
      result.failed++;
    }
  }

  // Voyage plan notes: same newest-wins rule the plan's own pull uses, where
  // a note with no local time loses to any account copy.
  for (const { key, note, ts } of notes) {
    const row = noteRows.get(key);
    if (row && (!ts || tsOf(row.saved_at) >= ts)) { result.notesSaved++; continue; }
    if (!row && !note.trim()) continue; // nothing to delete
    try {
      const { error } = await withTimeout(supabase.from("voyage_plan_day_notes").upsert({
        auth_user_id: uid, note_key: key, note, saved_at: new Date(ts || Date.now()).toISOString(),
      }, { onConflict: "auth_user_id,note_key" }), 10000);
      if (error) throw error;
      result.notesSaved++;
    } catch {
      result.failed++;
    }
  }

  // The reusable saved signature: a save made offline lives only here
  // (SavedSignatureControls). Carried only when the account has none, since
  // an account copy may be a newer one saved on another device.
  const sig = (() => { try { return localStorage.getItem(`savedSig_${uid}`); } catch { return null; } })();
  if (sig && sig.startsWith("data:image/")) {
    try {
      const { data, error } = await withTimeout(
        supabase.from("pilot_saved_signatures").select("auth_user_id").eq("auth_user_id", uid).limit(1), 8000);
      if (error) throw error;
      if (!data?.length) {
        const { error: upErr } = await withTimeout(supabase.from("pilot_saved_signatures").upsert(
          { auth_user_id: uid, signature_data: sig, updated_at: new Date().toISOString() },
          { onConflict: "auth_user_id" }), 10000);
        if (upErr) throw upErr;
        result.signatureSaved = true;
      }
    } catch {
      result.failed++;
    }
  }

  const movedAnything = slips.length || expenses.length || notes.length || result.filedSaved || result.signatureSaved;
  result.status = result.failed || result.unmatched ? "partial" : movedAnything ? "done" : "nothing";
  return result;
}

// Voyage plan state that only a voyage plan open at THIS address can finish
// (Codex on #606):
//   routes: alternate-route choices made with no signal, still waiting to be
//     shared. Opening that voyage plan here while connected sends them.
//   watchSettings: the watch calculator's settings (who, lengths, handover
//     moves). They are kept on the device by design, even after a watch is
//     posted; the posted watch itself is shared and safe. They cannot move.
export function voyagePlanDeviceOnly() {
  let routes = 0;
  let watchSettings = 0;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (k.startsWith("voyagePlanRoutesUnsynced_")) {
        const set = readJson(k);
        if (Array.isArray(set)) routes += set.length;
      } else if (k.startsWith("vpWatch_")) {
        const all = readJson(k);
        if (all && typeof all === "object" && Object.keys(all).length) watchSettings++;
      }
    }
  } catch { /* storage unavailable */ }
  return { routes, watchSettings };
}
